import { cva, type VariantProps } from 'class-variance-authority'
import { keysOf } from '../../lib/keys-of'

/**
 * The shell every text-entry control shares: Input, Select.Trigger, Combobox.Input. Box, ground,
 * edge and every state, so the three cannot drift apart on any of them.
 *
 * Four heights, and they are Button's own `sm`/`md`/`lg`/`xl`, so a field and a button in one row
 * line up without either knowing about the other. Button's `xs` (20) and `2xl` (48) are left out:
 * a 16px line box does not fit in a 20px box, and nothing in a form needs to be 48 tall.
 *
 * The corner is half the height, from `--radius-size-*`, and squircled, so it reads as a softened
 * rectangle rather than a pill.
 *
 * Type does not move with the size. Every height renders at 13px regular, because size here is a
 * density decision and not an emphasis one: a compact field in a toolbar and a comfortable one in a
 * form hold the same kind of content.
 *
 * Padding is its own ramp (9 / 9 / 11 / 13) rather than Button's (8 / 10 / 12 / 16). A button's
 * padding wraps a short label that has to read as a target, so it is generous on purpose. A
 * field's is dead space in front of content that can run the width of the box. It is set as
 * `--control-pad` so a trailing glyph can pull its side in.
 */
const SIZES = {
  sm: '[--control-h:24px] [--control-pad:9px] gap-1.5 rounded-[var(--radius-size-sm)]',
  md: '[--control-h:28px] [--control-pad:9px] gap-1.5 rounded-[var(--radius-size-md)]',
  lg: '[--control-h:32px] [--control-pad:11px] gap-2 rounded-[var(--radius-size-lg)]',
  xl: '[--control-h:40px] [--control-pad:13px] gap-2 rounded-[var(--radius-size-xl)]',
}

const controlShellVariants = cva(
  [
    'h-[var(--control-h)] px-[var(--control-pad)] corner-squircle text-sm font-normal text-foreground outline-none',
    'bg-background-raised border border-border hover:border-hover',
    'transition-[border-color,box-shadow] duration-150 ease-[cubic-bezier(0.22,1,0.36,1)]',
    // Focus turns the edge to the ring's colour, so the box and the halo past the gap read as one mark.
    'focus-visible:border-[color:var(--focus-ring-color)]',
    'is-invalid:border-[color:var(--danger)] is-invalid:hover:border-danger-hover',
    'is-invalid:focus-visible:border-[color:var(--danger)] is-invalid:focus-visible:focus-ring-danger',
    'is-disabled:cursor-not-allowed is-disabled:bg-element-disabled is-disabled:border-disabled is-disabled:text-foreground-disabled',
  ],
  {
    variants: {
      size: SIZES,
    },
    defaultVariants: {
      size: 'md',
    },
  },
)

type ControlShellSize = NonNullable<VariantProps<typeof controlShellVariants>['size']>

const CONTROL_SHELL_SIZES = keysOf(SIZES)

/**
 * A trailing icon button inside a shell has to be one size down, because that is the only size
 * that fits: a 24px shell leaves 22px of interior, so a 24px button cannot go in it.
 */
const CONTROL_SHELL_ACTION_SIZE: Record<ControlShellSize, 'xs' | 'sm'> = {
  sm: 'xs',
  md: 'sm',
  lg: 'sm',
  xl: 'sm',
}

export { controlShellVariants, CONTROL_SHELL_ACTION_SIZE, CONTROL_SHELL_SIZES }
export type { ControlShellSize }
